import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../api';

const TYPES = ['business','heritage','nature','event','emergency','tourism','education','transport','community','public'];
const TYPE_ICONS = { business:'🏢',heritage:'🏛️',nature:'🌿',event:'🎪',emergency:'🚑',tourism:'🗺️',education:'🎓',transport:'🚌',community:'🤝',public:'🏛️' };

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Syne:wght@700;800&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
.ap-root{min-height:100vh;background:linear-gradient(145deg,#06112b,#112359);font-family:'DM Sans',sans-serif;color:#e2e8f0;}
.ap-body{max-width:580px;margin:0 auto;padding:24px 16px 80px;}
.ap-back{display:inline-flex;align-items:center;gap:6px;color:#5aaef7;font-size:13px;text-decoration:none;font-weight:600;margin-bottom:20px;}
.ap-title{font-family:'Syne',sans-serif;font-size:24px;font-weight:800;color:#fff;margin-bottom:4px;}
.ap-sub{font-size:13px;color:#64748b;margin-bottom:24px;}
.ap-section{background:rgba(255,255,255,.04);border:1px solid rgba(28,115,186,.18);border-radius:16px;padding:18px;margin-bottom:16px;}
.ap-section-title{font-size:11px;font-weight:700;color:#5aaef7;text-transform:uppercase;letter-spacing:1.5px;margin-bottom:14px;}
.ap-field{margin-bottom:14px;}
.ap-label{display:block;font-size:10px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:1.2px;margin-bottom:5px;}
.ap-input{width:100%;padding:10px 13px;background:rgba(255,255,255,.05);border:1px solid rgba(28,115,186,.2);border-radius:9px;color:#e2e8f0;font-family:'DM Sans',sans-serif;font-size:13px;outline:none;transition:border .2s;}
.ap-input:focus{border-color:#1c73ba;}
.ap-textarea{width:100%;padding:10px 13px;background:rgba(255,255,255,.05);border:1px solid rgba(28,115,186,.2);border-radius:9px;color:#e2e8f0;font-family:'DM Sans',sans-serif;font-size:13px;outline:none;resize:vertical;transition:border .2s;}
.ap-select{width:100%;padding:10px 13px;background:rgba(6,17,43,.9);border:1px solid rgba(28,115,186,.2);border-radius:9px;color:#e2e8f0;font-family:'DM Sans',sans-serif;font-size:13px;outline:none;cursor:pointer;}
.ap-type-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(90px,1fr));gap:8px;}
.ap-type-btn{padding:10px 6px;border-radius:10px;border:1px solid rgba(255,255,255,.08);background:rgba(255,255,255,.03);text-align:center;cursor:pointer;transition:all .18s;}
.ap-type-btn.active{border-color:#1c73ba;background:rgba(28,115,186,.12);}
.ap-type-icon{font-size:22px;margin-bottom:4px;}
.ap-type-label{font-size:10px;color:#64748b;text-transform:capitalize;}
.ap-type-btn.active .ap-type-label{color:#5aaef7;}
.ap-coord-row{display:flex;gap:10px;}
.ap-coord-row .ap-input{flex:1;}
.ap-locate-btn{padding:10px 14px;border-radius:9px;border:1px solid rgba(28,115,186,.3);background:rgba(28,115,186,.1);color:#5aaef7;font-family:'DM Sans',sans-serif;font-size:13px;font-weight:600;cursor:pointer;white-space:nowrap;transition:all .18s;}
.ap-locate-btn:hover{background:rgba(28,115,186,.2);}
.ap-alert{padding:10px 14px;border-radius:9px;font-size:13px;margin-bottom:12px;}
.ap-alert.error{background:rgba(248,113,113,.1);border:1px solid rgba(248,113,113,.25);color:#f87171;}
.ap-alert.success{background:rgba(52,211,153,.1);border:1px solid rgba(52,211,153,.25);color:#34d399;}
.ap-submit{width:100%;padding:14px;border-radius:12px;border:none;background:linear-gradient(135deg,#1c73ba,#2b8de0);color:#fff;font-family:'DM Sans',sans-serif;font-size:15px;font-weight:700;cursor:pointer;transition:all .18s;margin-top:4px;}
.ap-submit:hover:not(:disabled){transform:translateY(-1px);}
.ap-submit:disabled{opacity:.6;cursor:not-allowed;}
`;

export default function AddPlace() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '', description: '', shortDesc: '', type: 'public',
    lat: '', lng: '', address: '', city: '', country: 'Nepal',
    coverPhoto: '', tags: '',
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');
  const [success,setSuccess]= useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const locateMe = () => {
    if (!navigator.geolocation) return setError('Geolocation not supported');
    navigator.geolocation.getCurrentPosition(
      pos => { set('lat', pos.coords.latitude.toFixed(6)); set('lng', pos.coords.longitude.toFixed(6)); },
      () => setError('Could not get location')
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!form.name.trim()) return setError('Name is required');
    if (!form.lat || !form.lng) return setError('Location coordinates are required. Use "My Location" or enter them manually.');
    setSaving(true);
    try {
      const payload = {
        ...form,
        lat: parseFloat(form.lat), lng: parseFloat(form.lng),
        tags: form.tags ? form.tags.split(',').map(t=>t.trim()).filter(Boolean) : [],
      };
      const { data } = await api.post('/places', payload);
      setSuccess(`"${data.name}" added to the map! ✅`);
      setTimeout(() => navigate('/explore'), 1500);
    } catch (e) { setError(e.response?.data?.error || 'Failed to add place'); }
    setSaving(false);
  };

  return (
    <div className="ap-root">
      <style>{CSS}</style>
      <div className="ap-body">
        <Link to="/explore" className="ap-back">← Back to Map</Link>
        <div className="ap-title">Add a Place</div>
        <p className="ap-sub">Add a business, heritage site, natural spot, or event to the Flap world map.</p>

        {error   && <div className="ap-alert error">⚠️ {error}</div>}
        {success && <div className="ap-alert success">{success}</div>}

        <form onSubmit={handleSubmit}>
          {/* Basic info */}
          <div className="ap-section">
            <div className="ap-section-title">Basic Info</div>
            <div className="ap-field">
              <label className="ap-label">Place Name *</label>
              <input className="ap-input" value={form.name} onChange={e=>set('name',e.target.value)} placeholder="e.g. Boudhanath Stupa" required/>
            </div>
            <div className="ap-field">
              <label className="ap-label">One-line description</label>
              <input className="ap-input" value={form.shortDesc} onChange={e=>set('shortDesc',e.target.value)} placeholder="Shows as tooltip on map"/>
            </div>
            <div className="ap-field">
              <label className="ap-label">Full description</label>
              <textarea className="ap-textarea" rows={3} value={form.description} onChange={e=>set('description',e.target.value)} placeholder="History, details, what to expect…"/>
            </div>
            <div className="ap-field">
              <label className="ap-label">Tags (comma-separated)</label>
              <input className="ap-input" value={form.tags} onChange={e=>set('tags',e.target.value)} placeholder="heritage, buddhist, UNESCO, stupa"/>
            </div>
          </div>

          {/* Type */}
          <div className="ap-section">
            <div className="ap-section-title">Place Type</div>
            <div className="ap-type-grid">
              {TYPES.map(t => (
                <div key={t} className={`ap-type-btn${form.type===t?' active':''}`} onClick={()=>set('type',t)}>
                  <div className="ap-type-icon">{TYPE_ICONS[t]}</div>
                  <div className="ap-type-label">{t}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Location */}
          <div className="ap-section">
            <div className="ap-section-title">Location *</div>
            <div className="ap-field">
              <label className="ap-label">Coordinates</label>
              <div className="ap-coord-row">
                <input className="ap-input" value={form.lat} onChange={e=>set('lat',e.target.value)} placeholder="Latitude (e.g. 27.7172)" type="number" step="any"/>
                <input className="ap-input" value={form.lng} onChange={e=>set('lng',e.target.value)} placeholder="Longitude (e.g. 85.3240)" type="number" step="any"/>
                <button type="button" className="ap-locate-btn" onClick={locateMe}>📍 My Location</button>
              </div>
              <div style={{fontSize:11,color:'#475569',marginTop:5}}>Tip: Right-click on Google Maps → "What's here?" to get coordinates</div>
            </div>
            <div className="ap-field">
              <label className="ap-label">Address</label>
              <input className="ap-input" value={form.address} onChange={e=>set('address',e.target.value)} placeholder="Street address or landmark"/>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
              <div className="ap-field">
                <label className="ap-label">City</label>
                <input className="ap-input" value={form.city} onChange={e=>set('city',e.target.value)} placeholder="Kathmandu"/>
              </div>
              <div className="ap-field">
                <label className="ap-label">Country</label>
                <input className="ap-input" value={form.country} onChange={e=>set('country',e.target.value)} placeholder="Nepal"/>
              </div>
            </div>
          </div>

          {/* Photo */}
          <div className="ap-section">
            <div className="ap-section-title">Cover Photo</div>
            <div className="ap-field">
              <label className="ap-label">Cover photo URL</label>
              <input className="ap-input" value={form.coverPhoto} onChange={e=>set('coverPhoto',e.target.value)} placeholder="https://...jpg"/>
            </div>
            {form.coverPhoto && <img src={form.coverPhoto} alt="preview" style={{width:'100%',height:140,objectFit:'cover',borderRadius:10,marginTop:4}}/>}
          </div>

          <button type="submit" className="ap-submit" disabled={saving}>
            {saving ? '⏳ Adding to map…' : '🗺️ Add to World Map'}
          </button>
        </form>
      </div>
    </div>
  );
}